import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { MaximizeIcon, MinimizeIcon } from '../../../assets/icons';
import {
  ChartActionId,
  ResolutionString,
  SeriesType,
} from '../../../charting_library/charting_library';
import { ASSET_ROUTE_URL } from '../../../constants/routes';
import { DEFAULT_CHART_TYPE, DEFAULT_RESOLUTION } from '../../../constants/tradingview';
import { ProductType } from '../../../hooks/useProducts';
import { useTailwindBreakpoint } from '../../../hooks/useTailwindBreakpoint';
import { cn } from '../../../lib/classNameHelpers';
import { localStorageKeys } from '../../../lib/localstorageKeys';
import { DEFAULT_CHART_PRICE_TYPE, type ChartPriceType } from '../../../lib/tradingViewHelpers';
import { Card } from '../../Card';
import { Button } from '../../catalyst-ui-kit/button';
import { DepthChart } from '../../DepthChart/DepthChart';
import { FundingHistory } from '../../FundingHistory/FundingChart';
import { DEFAULT_FUNDING_CHART_TIMESCALE, FundingTimescale } from '../../FundingHistory/timescales';
import { InlineSelect } from '../../InlineSelect/InlineSelect';
import { TradingViewChart } from '../../TradingViewChart/TradingViewChart';
import { TradeFavorites } from '../TradeFavorites/TradeFavorites';
import { TradeHeader } from '../TradeHeader/TradeHeader';
import { TradeOrderbookTabs } from '../TradeOrderbookTabs/TradeOrderbookTabs';
import { TradePageWrapper } from '../TradePageWrapper';
import { MarketDetails } from './MarketDetails';
import { TradeChartsHeader } from './TradeChartsHeader';

type TradeChartsProps = {
  product: ProductType;
  className?: string;
};

export const TradeCharts = ({ product, className }: TradeChartsProps) => {
  const navigate = useNavigate({ from: ASSET_ROUTE_URL });
  const isLg = useTailwindBreakpoint('lg');
  const isSm = useTailwindBreakpoint('sm');
  const { chartTab } = useSearch({ from: ASSET_ROUTE_URL });
  const is2xl = useTailwindBreakpoint('2xl');

  // Chart state management
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chartActionQueue, setChartActionQueue] = useState<ChartActionId[]>([]);
  const [chartResolution, setChartResolution] = useLocalStorage<ResolutionString>(
    localStorageKeys.chartResolution,
    DEFAULT_RESOLUTION
  );
  const [chartType, setChartType] = useLocalStorage<SeriesType>(
    localStorageKeys.chartType,
    DEFAULT_CHART_TYPE
  );
  const [priceType, setPriceType] = useLocalStorage<ChartPriceType>(
    localStorageKeys.chartDisplayPrice,
    DEFAULT_CHART_PRICE_TYPE
  );

  // Funding chart state
  const [fundingTimescale, setFundingTimescale] = useState<FundingTimescale>(
    DEFAULT_FUNDING_CHART_TIMESCALE
  );

  const pushChartAction = useCallback((action: ChartActionId) => {
    setChartActionQueue((prev) => [...prev, action]);
  }, []);

  const handleFullscreenToggle = useCallback(async () => {
    if (!chartContainerRef.current) return;

    try {
      if (!isFullscreen) {
        await chartContainerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('Failed to toggle fullscreen:', error);
    }
  }, [isFullscreen]);

  // We only want to show the TradingView chart on mobile
  useEffect(() => {
    if (!isSm && chartTab !== 'price') {
      void navigate({ search: (prev) => ({ ...prev, chartTab: 'price' }) });
    }
  }, [isSm, chartTab, navigate]);

  return (
    <TradePageWrapper className={cn('flex flex-col gap-1 lg:flex-row', className)}>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {is2xl && <TradeFavorites />}
        <div className="flex min-w-0 grow gap-1">
          <div className="flex min-w-0 grow flex-col gap-1">
            {is2xl && <TradeHeader product={product} />}
            <Card className="relative flex min-w-0 flex-1 flex-col">
              <div
                className="flex flex-1 flex-col rounded-lg bg-backgroundPrimary"
                ref={chartContainerRef}
              >
                <div className="flex flex-wrap justify-between border-b border-borderPrimary">
                  <TradeChartsHeader
                    chartResolution={chartResolution}
                    chartType={chartType}
                    setChartResolution={setChartResolution}
                    setChartType={setChartType}
                    priceType={priceType}
                    setPriceType={setPriceType}
                    onIndicatorsClick={() => pushChartAction('insertIndicator')}
                    chartTab={chartTab}
                    fundingTimescale={fundingTimescale}
                    setFundingTimescale={setFundingTimescale}
                    // Allow the header to scroll
                    className="min-w-0"
                  />
                  <div className="flex items-center py-2 pl-0.5 pr-2 md:gap-2">
                    {isSm && (
                      <InlineSelect
                        className="gap-0"
                        buttonClassName="!px-1"
                        selected={chartTab}
                        options={
                          [
                            { value: 'price', label: 'Price' },
                            { value: 'funding', label: 'Funding' },
                            { value: 'depth', label: 'Depth' },
                            { value: 'details', label: 'Details' },
                          ] as const
                        }
                        onChange={(option) =>
                          option &&
                          navigate({ search: (prev) => ({ ...prev, chartTab: option.value }) })
                        }
                      />
                    )}
                    <Button
                      text
                      size="xs"
                      onClick={handleFullscreenToggle}
                      className="!p-1 !text-contentSecondary hover:!text-contentPrimary"
                      title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                    >
                      {isFullscreen ? (
                        <MinimizeIcon className="size-4" />
                      ) : (
                        <MaximizeIcon className="size-4" />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="grid min-h-0 grow">
                  <TradingViewChart
                    product={product}
                    chartResolution={chartResolution}
                    chartType={chartType}
                    priceType={priceType}
                    className={chartTab === 'price' ? '' : 'hidden'}
                    setChartResolution={setChartResolution}
                    setChartType={setChartType}
                    chartActionQueue={chartActionQueue}
                    setChartActionQueue={setChartActionQueue}
                  />
                  {chartTab === 'funding' && (
                    <FundingHistory productId={product.id} timescale={fundingTimescale} />
                  )}
                  {chartTab === 'depth' && <DepthChart product={product} />}
                  {chartTab === 'details' && <MarketDetails product={product} />}
                </div>
              </div>
            </Card>
          </div>
          {isLg && <TradeOrderbookTabs />}
        </div>
      </div>
    </TradePageWrapper>
  );
};
